package com.neurobs.js.agora

import com.neurobs.ccab.{AgoraTokenData, CaasType, ChatClientId}
import com.neurobs.util.{ChatConnectionStatus, ChatSession, ChatSessionFactory, ChatSessionNotify, CommunicationState, Logger}
import com.neurobs.js.dom.VideoElement
import io.circe._
import io.circe.parser._

import scala.scalajs.js
import scala.concurrent._
import ExecutionContext.Implicits.global
import scala.scalajs.js.timers._
import scala.util.{Failure, Success, Try}

class AgoraChatSession (rtcClient: IAgoraRTCClient, rtmClient: RTMClient, channelName: String, notify: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement)(implicit logger: Logger) extends ChatSession {

	override def apiType: CaasType.Value = CaasType.Agora

	private val controllerConnectionType = "t"
	private val adminConnectionType = "e"


	rtmClient.on(
		"ConnectionStateChanged",
		(newState:String, reason:String) => {
	//		println("connection state changed to " + newState + " reason: " + reason)
			if(newState == "CONNECTED")
				setStatus(ChatConnectionStatus.Connected, None)
			else if(newState == "ABORTED" || newState == "DISCONNECTED")
				setStatus(ChatConnectionStatus.Closed, Some(reason))
			else if(newState == "RECONNECTING")
				setStatus(ChatConnectionStatus.Disconnected, Some(reason))
			else if(newState == "CONNECTING")
				setStatus(ChatConnectionStatus.Connecting, Some(reason))
		}
	)

	rtcClient.on(
		event = "user-published",
		listener = (user:IAgoraRTCRemoteUser, mediaType:String) => {

			if(mediaType == "video") {
				val sub_future = rtcClient.subscribe(user, mediaType).toFuture
				sub_future.onComplete {
					case scala.util.Success(_) =>
						val uidSplit = user.uid.split(",")
						if (uidSplit.size >=2 && uidSplit(1) == controllerConnectionType) {
							user.videoTrack.toOption.get.play(remoteVideoElement.element)
							setState(state.copy(remoteVideo = true))
						}
						if (uidSplit.size >=2 && uidSplit(1) == adminConnectionType) {
							setPublishing(audio = false, video = false)
							localVideoElement.setSize(0, 0)
							remoteAdminVideoElement.setSize(300, 300)
							user.videoTrack.toOption.get.play(remoteAdminVideoElement.element)

							setState(state.copy(localVideo = false))
						}
					case scala.util.Failure(_) =>
						setState(state.copy(remoteVideo = false))

				}
			}

			if(mediaType == "audio") {
				val sub_future = rtcClient.subscribe(user, mediaType).toFuture
				sub_future.onComplete {
					case scala.util.Success(_) =>
						val uidSplit = user.uid.split(",")
						if (uidSplit.size >=2 && uidSplit(1) == controllerConnectionType) {
							user.audioTrack.toOption.get.play()
							user.audioTrack.toOption.get.setVolume(subjectVolume)
							setState(state.copy(remoteAudio = true))
						}
						if (uidSplit.size >=2 && uidSplit(1) == adminConnectionType) {
							setPublishing(audio = false, video = false)
							user.audioTrack.toOption.get.play()
							user.audioTrack.toOption.get.setVolume(subjectVolume)
							setState(state.copy(localAudio = false))
						}
					case scala.util.Failure(_) =>
						setState(state.copy(remoteAudio = false))
				}

			}
		}
	)

	rtcClient.on(
		event = "user-unpublished",
		listener = (user:IAgoraRTCRemoteUser, mediaType:String) => {
			val uidSplit = user.uid.split(",")

			if(mediaType == "audio") {
				if (uidSplit.size >=2 && uidSplit(1) == controllerConnectionType) {
					setState(state.copy(remoteAudio = false))
				}
				if (uidSplit.size >=2 && uidSplit(1) == adminConnectionType) {
				}


			}
			if(mediaType == "video") {
				if (uidSplit.size >=2 && uidSplit(1) == controllerConnectionType) {
					remoteVideoElement.element.innerHTML = ""
					setState(state.copy(remoteVideo = false))
				}
				if (uidSplit.size >=2 && uidSplit(1) == adminConnectionType) {
					remoteAdminVideoElement.element.innerHTML = ""
				}
			}
		}
	)


	var rtmChannel: Option[RtmChannel] = None
	private def setRtmChannel(newRtmChannel: Option[RtmChannel]): Unit = {
		rtmChannel = newRtmChannel
	}

	var microphoneAudioTrack: Option[IMicrophoneAudioTrack] = None
	var cameraVideoTrack: Option[ICameraVideoTrack] = None
	private def setTracks(newMicTrack: Option[IMicrophoneAudioTrack], newCamTrack: Option[ICameraVideoTrack] ): Unit = {
		microphoneAudioTrack = newMicTrack
		cameraVideoTrack = newCamTrack
	}

	private var status = ChatConnectionStatus.Connecting
	override def connectionStatus: ChatConnectionStatus.Value = status
	private def setStatus(newStatus: ChatConnectionStatus.Value, error: Option[String]): Unit = {
		if (newStatus != status) {
			status = newStatus
			notify.connectionStatusChanged(newStatus, error)
		}
	}

	def escape(s: String): String = s.flatMap(escapedChar)

	def escapedChar(ch: Char): String = ch match {
		case '\b' => "\\b"
		case '\f' => "\\f"
		case '\n' => "\\n"
		case '\r' => "\\r"
		case '\t' => "\\t"
		case '"'  => "\\\""
		case '\\' => "\\\\"
		case '/' => "\\/"
		case _ => String.valueOf(ch)
	}

	def sender(message: String, messageType: String, iteration: Int): Unit = {
		if(rtmChannel.isDefined) {
			var mess = message
			//message is either valid JSON block or generic string. JSONify generic string
			if(!message.startsWith("{")) {
				mess = "\"" + escape(mess) + "\""
			}

			val rtmMessage = RtmMessage(text = "{\"message\":" + mess + ",\"type\":\"" + messageType + "\"}")
			val future = rtmChannel.get.sendMessage(rtmMessage).toFuture
			future.onComplete {
				case scala.util.Success(_) =>
					if(!message.startsWith("{")) {
						//so that outgoing admin messages appear in the chat
						val sp = rtcClient.uid.split(",")
						if(sp.length > 2) {
							notify.messageReceived(message = message, "AdminChat", source = sp(2))
						} else {
							//fallback, shouldn't happen
							notify.messageReceived(message = message, "AdminChat", source = "Admin")
						}
					}
				case scala.util.Failure(ex) =>
					notify.genericError("Message could not be sent: " + ex.getMessage)

			}
		} else if( iteration < 5) {
				setTimeout(1000) {
				sender(message, messageType, iteration + 1)
			}
		} else {
				notify.genericError("Message could not be sent: rtmChannel is not defined")
		}

	}

	override def sendMessage(message: String, messageType: String): Unit = {
		logger.debug(s"Agora sending: $message")
		sender(message, messageType, 0)
	}

	private var publishingAudio: Boolean = false
	private var publishingVideo: Boolean = false
	override def isPublishingAV: Boolean = publishingVideo || publishingAudio

	override def setPublishing(audio: Boolean = publishingAudio, video: Boolean = publishingVideo): Unit = {
		//println("AgoraChatSession::setPublishing: " + audio + " " + video)

		if (microphoneAudioTrack.isDefined) {
			if (audio) {
				rtcClient.publish(microphoneAudioTrack.get)
				setState(state.copy(localAudio = true))
				publishingAudio = true
			} else {
				rtcClient.unpublish(microphoneAudioTrack.get)
				setState(state.copy(localAudio = false))
				publishingAudio = false
			}
		}

		if (cameraVideoTrack.isDefined) {
			if (video) {
				rtcClient.publish(cameraVideoTrack.get)
			  	//val stats = rtcClient.getLocalVideoStats()
				//val width = rtcClient.captureResolutionWidth
				//val height = rtcClient.captureResolutionHeight
				remoteAdminVideoElement.setSize(0,0)
				localVideoElement.setSize(300, 300)

				cameraVideoTrack.get.play(localVideoElement.element)
				setState(state.copy(localVideo = true))
				publishingVideo = true
			} else {
				localVideoElement.element.innerHTML = ""
				localVideoElement.setSize(widthPx = 0, heightPx = 0)
				rtcClient.unpublish(cameraVideoTrack.get)
				setState(state.copy(localVideo = false))
				publishingVideo = false
			}
		}
	}

	override def close(): Unit = {
		rtcClient.leave()
	}

	var subjectVolume: Double = 100
	override def setPlaybackVolume(volume: Double): Unit = {
		subjectVolume = volume*10
		for(user <- rtcClient.remoteUsers) {
			if(user.audioTrack.isDefined) {
				user.audioTrack.get.setVolume(subjectVolume)
			}
		}

	}

	private var state: CommunicationState = CommunicationState()
	private def setState(newState: CommunicationState): Unit = {
		if (newState != state) {
			notify.communicationStateChanged(newState)
			state = newState
		}
	}


}

class AgoraChatSessionFactory(tokenData: AgoraTokenData)(implicit logger: Logger) extends ChatSessionFactory {
	override def connect(thisClient: ChatClientId, notify: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement): Either[String, ChatSession] = {
		AgoraChatSession(tokenData, thisClient, notify, localVideoElement, remoteVideoElement, remoteAdminVideoElement)
	}
}

object AgoraChatSession {


	var microphoneAudioTrack: Option[IMicrophoneAudioTrack] = None
	var cameraVideoTrack: Option[ICameraVideoTrack] = None
	var rtmChannel: Option[RtmChannel] = None
	var rtcClient: Option[IAgoraRTCClient] = None
	var rtmClient: Option[RTMClient] = None
	var tokenData: Option[AgoraTokenData] = None
	var notifier: Option[ChatSessionNotify] = None

	def apply(tokenData_in: AgoraTokenData, thisClient: ChatClientId, notify_in: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement)(implicit logger: Logger): Either[String, AgoraChatSession] = {
		Try {
			tokenData = Some(tokenData_in)
			notifier = Some(notify_in)
			rtcClient = Some(AgoraRTC.createClient(js.Dynamic.literal(mode = "rtc", codec = "vp8")))
			rtmClient = Some(AgoraRTM.createInstance(tokenData_in.appId, js.Dynamic.literal()))


			AgoraRTM.setArea(js.Dynamic.literal(areaCodes = js.Array("NORTH_AMERICA") ))

			var remoteUsers:Map[IAgoraRTCRemoteUser, ChatClientId] = Map()

			rtcClient.get.on(
				event = "user-joined",
				listener = (user:IAgoraRTCRemoteUser) => {
					val split = user.uid.split(",")

					if(split.length > 2) {
						val chatId = ChatClientId(uniqueId = split(0), `type` = split(1), user = split(2))
						remoteUsers += (user -> chatId)
						notifier.get.remoteClientConnected(chatId)
					} else {
						notifier.get.subscribeToRemoteError("Unable to parse user.uid: " + user.uid)
					}
				}
			)

			rtcClient.get.on(
				event = "user-left",
				listener = (user:IAgoraRTCRemoteUser, reason: String) => {
					notifier.get.remoteClientDisconnected(remoteUsers(user))
					remoteUsers -= user
				}
			)

			val socket = new AgoraChatSession(rtcClient = rtcClient.get, rtmClient = rtmClient.get, channelName = tokenData.get.channelName,
				notify = notifier.get, localVideoElement = localVideoElement, remoteVideoElement = remoteVideoElement,
				remoteAdminVideoElement = remoteAdminVideoElement)

			////not currently using
			//val get_cameras_future = AgoraRTC.getCameras(false).toFuture
			//val get_microphones_future = AgoraRTC.getMicrophones(false).toFuture
			//val getDevicesFutures = List(get_cameras_future, get_microphones_future)
			////

			loadingAsyncChainPart1(socket)

			socket

		} match {
			case Success(value) => Right(value)
			case Failure(e) => Left(e.getLocalizedMessage)
		}

		}

		private def loadingAsyncChainPart1(socket: AgoraChatSession): Unit = {
			val camera_track_future = AgoraRTC.createCameraVideoTrack().toFuture
			camera_track_future.onComplete({
				case Failure(e) =>
					cameraVideoTrack = None
					loadingAsyncChainPart2(socket)
				case Success(track) =>
					cameraVideoTrack = Some(track)
					loadingAsyncChainPart2(socket)
			})
		}

		private def loadingAsyncChainPart2(socket: AgoraChatSession): Unit = {
			val microphone_track_future = AgoraRTC.createMicrophoneAudioTrack().toFuture
			microphone_track_future.onComplete({
				case Failure(e) =>
					microphoneAudioTrack = None
					loadingAsyncChainPart3(socket)
				case Success(track) =>
					microphoneAudioTrack = Some(track)
					loadingAsyncChainPart3(socket)
			})
		}

		private def loadingAsyncChainPart3(socket: AgoraChatSession): Unit = {
			if (cameraVideoTrack == None) {
				notifier.get.publishAVError("There are no administrator video cameras available.")
			}
			if (microphoneAudioTrack == None) {
				notifier.get.publishAVError("There are no administrator microphones available.")
			}

			socket.setTracks(microphoneAudioTrack, cameraVideoTrack)

			val rtc_join_future = rtcClient.get.join(tokenData.get.appId, tokenData.get.channelName, tokenData.get.rtcToken, tokenData.get.userId).toFuture
			val rtm_join_future =	rtmClient.get.login(js.Dynamic.literal(token = tokenData.get.rtmToken, uid = tokenData.get.userId)).toFuture
			val initializationFutures = List(rtc_join_future, rtm_join_future)

			Future.sequence(initializationFutures).onComplete({
				case Failure(e) =>
					notifier.get.publishAVError(e.getMessage)
				case Success(result_list) =>
					rtmChannel = Some(rtmClient.get.createChannel(tokenData.get.channelName))
					val join_future = rtmChannel.get.join().toFuture
					join_future.onComplete {
						case scala.util.Success(_) =>
							rtmChannel.get.on(
								eventName = "ChannelMessage",
								listener = (message: RtmMessage, memberId: String) => {
									parse(message.text) match {
										case Right(json) =>
											val cursor: HCursor = json.hcursor
											val mess_string = cursor.downField("message").as[String].toOption
											val mess_json = cursor.downField("message").as[Json].toOption
											val typ = cursor.downField("type").as[String].toOption

											var member = memberId
											val split = memberId.split(",")
											if (split.size > 2)
												member = split(2)

											if (mess_string.isDefined && typ.isDefined) {
												if (typ.get == "Controller") {
													notifier.get.messageReceived(message = mess_string.get, messageType = typ.get, source = "Controller")
												} else {
													notifier.get.messageReceived(message = mess_string.get, messageType = typ.get, source = member)
												}
											} else if (mess_json.isDefined && typ.isDefined) {
												if (typ.get == "Controller") {
													notifier.get.messageReceived(message = mess_json.get.noSpaces, messageType = typ.get, source = "Controller")
												} else {
													notifier.get.messageReceived(message = mess_json.get.noSpaces, messageType = typ.get, source = member)
												}
											} else {
												notifier.get.genericError("Unable to find \"message\" and \"type\" in JSON: " + message.text)
											}
										case Left(_) => notifier.get.genericError("Unable to parse message JSON: " + message.text)
									}

								}
							)
							socket.setRtmChannel(rtmChannel)
						case scala.util.Failure(e) =>
							notifier.get.genericError(e.getMessage)
					}

			  })
			}


}